import Vue from 'vue';
import VueMeta from 'vue-meta';
import * as Sentry from '@sentry/vue';
import { Integrations } from '@sentry/tracing';
import {
  CaptureConsole as CaptureConsoleIntegration,
  ExtraErrorData as ExtraErrorDataIntegration,
  Offline as OfflineIntegration
} from '@sentry/integrations';
import get from 'lodash/get';
import set from 'lodash/set';

import './setup';
import { serviceContainer } from './di';
import store from './store/index';
import GTM from './plugins/gtm';
import AutopayChatPlugin from './plugins/autopay-chat';
import VueAutopilot from './plugins/autopilot/vue-autopilot';
import { getAndSetLeadChannel } from './utilities/getAndSetLeadChannel';
import { sanitizeRequestBody } from './utilities/sanitizeRequestBody';
import { storage } from './utilities/storage';

// this will run after Sentry has handled the error
Vue.config.errorHandler = (error, vm, info) => {
  if (process.env.APP_ENV === 'local') {
    console.error(error, vm, info);
  }
};

Vue.use(VueAutopilot, {
  token: process.env.AUTOPILOT_TOKEN,
  window,
  url: process.env.AUTOPILOT_URL
});

Vue.use(GTM, {
  gtmId: process.env.GTM_ID,
  appEnvironment: process.env.APP_ENV,
  window
});

Vue.use(VueMeta);

// Vue Router
const router = serviceContainer.vueRouter;

// Attach some of our services to vue instance
Vue.use((Vue) => {
  // dependency injection attached to vue instance as $di
  Vue.prototype.$di = serviceContainer;

  // instantiate the service to set attribution
  serviceContainer.get('ingressService');
});

router.beforeEach((to, from, next) => {
  const environment = process.env.APP_ENV;

  // hook into GTM dataLayer for pageview logging
  if (to && from && to.path !== from.path) {
    window && window.dataLayer && window.dataLayer.push({ event: 'pageview' });
  }

  if (environment === 'local') {
    next();
    return;
  }

  const stateCheck = store.getters['global/hasSeenHome'];
  const guarded = !to.matched.some(record => record.meta.unguarded);

  if (guarded && !stateCheck) next({ name: 'forms.home' });
  else next();
});

// console levels to capture in Sentry
const levels = process.env.APP_ENV === 'prod'
  ? ['error']
  : ['warn', 'error'];

// Adjust this value as needed for production...
const tracesSampleRate = process.env.APP_ENV === 'prod'
  ? 0.4
  : 1.0;

Sentry.init({
  Vue,
  autoSessionTracking: process.env.SENTRY_AUTO_SESSION_TRACKING === 'true',
  dsn: process.env.SENTRY_DSN,
  debug: process.env.SENTRY_DEBUG === 'true',
  environment: process.env.APP_ENV,
  ignoreErrors: [
    'Non-Error promise rejection captured'
  ],
  integrations: [
    new Integrations.BrowserTracing({
      routingInstrumentation: Sentry.vueRouterInstrumentation(router),
      tracingOrigins: [
        /.*autopay\.com/g,
        /.*hotjar\.com/g,
        /.*google-analytics\.com/g
      ],
      idleTimeout: 5000
    }),
    new ExtraErrorDataIntegration({ depth: 5 }),
    new CaptureConsoleIntegration({ levels }),
    new OfflineIntegration()
  ],
  logErrors: process.env.APP_ENV !== 'prod',
  tracesSampleRate,

  // list of top-level components used by the router
  trackComponents: [
    'App',
    'BrowserNotSupported',
    'ContactJoint',
    'ContactMain',
    'Details',
    'EmploymentJoint',
    'EmploymentMain',
    'Home',
    'LandingPage',
    'Make',
    'Model',
    'PageNotFound',
    'Pre',
    'Promo',
    'ResidenceJoint',
    'ResidenceMain',
    'Review',
    'Thanks',
    'Trim',
    'Year'
  ],

  beforeSend (event) {
    [
      'contexts.Error.config.data',
      'config.data',
      'extra.config.data',
      'extra.request.__sentry_xhr__.body'
    ]
      .filter((eventPath) => get(event, eventPath))
      .forEach((eventPath) => {
        set(event, eventPath, sanitizeRequestBody(get(event, eventPath)));
      });

    // append current feature flag states to Sentry reports
    event.extra = {
      ...event.extra,
      ...store.getters['feature/currentFeatureFlagStates']
    };

    return event;
  }
});

store.dispatch('global/setOriginMarketingSourceUrl', window.location.href);

const leadChannel = getAndSetLeadChannel()();

store.dispatch('feature/start')
  .then(() => store.dispatch('brand/loadBrandInformation', leadChannel))
  .then(() => {
    // cache the workflow in local storage for future use
    storage.set('activeFlow', store.getters['brand/workflow']);

    // inform the workflow service of the change
    serviceContainer
      .get('workflowService')
      .setActiveFlow(store.getters['brand/workflow']);
  })
  .then(() => {
    if (store.getters['feature/isPodiumChatWidgetEnabled']) {
      Vue.use(AutopayChatPlugin, {
        window,
        document
      });
    }
  })
  .then(() => {
    // finally, boot up the application
    window.fastTrack = new Vue({ router, store }).$mount('#app');

    const successEvent = document.createEvent('Event');
    successEvent.initEvent('FastTrackLoaded', true, true);
    document.dispatchEvent(successEvent);
  })
  .catch((e) => {
    Sentry.captureException(e);
    const failureEvent = document.createEvent('Event');
    failureEvent.initEvent('FastTrackLoadFailed', true, true);
    document.dispatchEvent(failureEvent);
  });
